import { useId } from "react";

import { ChartLegend } from "../ChartLegend";

import {
    DONUT_CIRCUMFERENCE,
    DONUT_RADIUS,
    DONUT_SIZE,
    DONUT_THICKNESS,
} from "./const";
import * as theme from "./DonutChart.css";

export interface DonutChartSegment {
    key: string;
    label: string;
    value: number;
    color: string;
    href?: string;
}

export interface DonutChartProps {
    segments: readonly DonutChartSegment[];
    ariaLabel: string;
    totalLabel: string;
    emptyLabel: string;
    onNavigate?: (href: string) => void;
}

export function DonutChart({
    segments,
    ariaLabel,
    totalLabel,
    emptyLabel,
    onNavigate,
}: DonutChartProps) {
    const maskId = useId();
    const visibleSegments = segments.filter(({ value }) => value > 0);
    const total = visibleSegments.reduce((sum, { value }) => sum + value, 0);
    const arcs = getArcs(visibleSegments, total);

    return (
        <div className={theme.root}>
            <div className={theme.chart}>
                <svg
                    className={theme.svg}
                    width={DONUT_SIZE}
                    height={DONUT_SIZE}
                    viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}
                    role="group"
                    aria-label={ariaLabel}
                >
                    <defs>
                        <mask id={maskId}>
                            <circle
                                className={theme.reveal}
                                cx={DONUT_SIZE / 2}
                                cy={DONUT_SIZE / 2}
                                r={DONUT_RADIUS}
                                stroke="white"
                                strokeWidth={DONUT_THICKNESS + 8}
                                strokeDasharray={DONUT_CIRCUMFERENCE}
                                transform={`rotate(-90 ${DONUT_SIZE / 2} ${DONUT_SIZE / 2})`}
                            />
                        </mask>
                    </defs>
                    <circle
                        className={theme.track}
                        cx={DONUT_SIZE / 2}
                        cy={DONUT_SIZE / 2}
                        r={DONUT_RADIUS}
                        strokeWidth={DONUT_THICKNESS}
                    />
                    <g mask={`url(#${maskId})`}>
                        {arcs.map((arc) => (
                            <SegmentArc
                                key={arc.key}
                                arc={arc}
                                onNavigate={onNavigate}
                            />
                        ))}
                    </g>
                </svg>
                <div className={theme.center}>
                    <div
                        className={theme.centerContent}
                        data-testid="donut-center"
                    >
                        <span className={theme.value}>
                            {total.toLocaleString()}
                        </span>
                        <span className={theme.label}>{totalLabel}</span>
                    </div>
                </div>
            </div>
            {visibleSegments.length === 0 ? (
                <div className={theme.emptyMessage}>{emptyLabel}</div>
            ) : (
                <div className={theme.legend}>
                    <ChartLegend
                        items={visibleSegments}
                        layout="list"
                        onNavigate={onNavigate}
                    />
                </div>
            )}
        </div>
    );
}

const VISIBLE_GAP_RADIANS = 0.06;

interface Arc extends DonutChartSegment {
    dashLength: number;
    rotation: number;
}

function getArcs(segments: readonly DonutChartSegment[], total: number): Arc[] {
    if (segments.length === 0) {
        return [];
    }

    const capRadians = DONUT_THICKNESS / DONUT_RADIUS;
    const gapRadians =
        segments.length === 1 ? 0 : VISIBLE_GAP_RADIANS + capRadians;
    const availableRadians = 2 * Math.PI - gapRadians * segments.length;
    let startRadians = -Math.PI / 2 + gapRadians / 2;

    return segments.map((segment) => {
        const sweepRadians = availableRadians * (segment.value / total);
        const arc = {
            ...segment,
            dashLength: DONUT_RADIUS * sweepRadians,
            rotation: (startRadians * 180) / Math.PI,
        };
        startRadians += sweepRadians + gapRadians;
        return arc;
    });
}

interface SegmentArcProps {
    arc: Arc;
    onNavigate?: (href: string) => void;
}

function SegmentArc({ arc, onNavigate }: SegmentArcProps) {
    const href = arc.href;
    const circle = (
        <circle
            className={theme.segment}
            data-segment-key={arc.key}
            cx={DONUT_SIZE / 2}
            cy={DONUT_SIZE / 2}
            r={DONUT_RADIUS}
            stroke={arc.color}
            strokeWidth={DONUT_THICKNESS}
            strokeDasharray={`${arc.dashLength} ${DONUT_CIRCUMFERENCE - arc.dashLength}`}
            transform={`rotate(${arc.rotation} ${DONUT_SIZE / 2} ${DONUT_SIZE / 2})`}
        />
    );

    return href ? (
        <a
            href={href}
            aria-hidden="true"
            tabIndex={-1}
            onClick={
                onNavigate
                    ? (event) => {
                          event.preventDefault();
                          onNavigate(href);
                      }
                    : undefined
            }
        >
            {circle}
        </a>
    ) : (
        circle
    );
}
